import { useState } from 'react';
import { C, FONTS } from '../../tokens/design';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import Skeleton from '../../components/ui/Skeleton';
import PageError from '../../components/ui/PageError';
import { useUserProfile } from '../../hooks/useAccountData';
import { useAuth } from '../../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

function Toggle({ label, sub, on, onToggle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${C.b2}` }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, color: C.t1, fontFamily: FONTS.sans }}>{label}</div>
        {sub && <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 2 }}>{sub}</div>}
      </div>
      <div onClick={onToggle} style={{ width: 36, height: 18, borderRadius: 9, background: on ? C.grn : C.t3, cursor: 'pointer', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}>
        <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: on ? 20 : 2, transition: 'left 0.15s' }} />
      </div>
    </div>
  );
}

export default function AccountSettings() {
  const { data: profile, loading, error, refetch } = useUserProfile();
  const { user } = useAuth();
  const [sessions, setSessions] = useState(true);
  const [ipLock, setIpLock] = useState(false);
  const [ipRanges, setIpRanges] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');

  const roleName = profile ? ({ 0: 'MSP Admin', 1: 'Security Analyst', 2: 'Account Manager', 3: 'Read-Only' }[profile.role] || 'User') : (user?.role || 'User');
  const email = profile?.email || user?.email || '\u2014';
  const mspName = profile?.mspName || '\u2014';
  const createdAt = profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : '\u2014';

  const handlePasswordReset = async () => {
    if (!email || email === '\u2014') return;

    setResetLoading(true);
    setResetMessage('');
    setResetError('');

    try {
      const resp = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || 'Unable to start password reset.');
      setResetMessage(data.message || 'Password reset link sent.');
    } catch (err) {
      setResetError(err.message || 'Unable to start password reset.');
    } finally {
      setResetLoading(false);
    }
  };
  if (loading) {
    return (
      <Shell title="Account Settings" sub="Password, MFA & security configuration">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Card style={{ padding: 22 }}><Skeleton width="50%" height={13} style={{ marginBottom: 16 }} />{[0,1,2,3,4].map(i => <Skeleton key={i} height={20} style={{ marginBottom: 10 }} />)}</Card>
            <Card style={{ padding: 22 }}><Skeleton width="60%" height={13} style={{ marginBottom: 14 }} /><Skeleton height={40} /></Card>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Card style={{ padding: 22 }}><Skeleton width="50%" height={13} style={{ marginBottom: 14 }} /><Skeleton height={24} style={{ marginBottom: 10 }} /><Skeleton height={24} /></Card>
            <Card style={{ padding: 22 }}><Skeleton width="50%" height={13} style={{ marginBottom: 10 }} /><Skeleton height={40} /></Card>
            <Card style={{ padding: 22 }}><Skeleton width="50%" height={13} style={{ marginBottom: 12 }} />{[0,1,2,3].map(i => <Skeleton key={i} height={14} style={{ marginBottom: 6 }} />)}</Card>
          </div>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell title="Account Settings" sub="Password, MFA & security configuration">
        <PageError error={error} onRetry={refetch} />
      </Shell>
    );
  }

  return (
    <Shell title="Account Settings" sub="Password, MFA & security configuration">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 16, color: C.t1 }}>Account Information</div>
            {[
              ['Email', email],
              ['Role', roleName],
              ['Organization', mspName],
              ['Member Since', createdAt],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.b2}` }}>
                <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{l}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, fontFamily: FONTS.mono }}>{v}</span>
              </div>
            ))}
            <div style={{ marginTop: 14, padding: 12, background: C.s2, borderRadius: 6, border: `1px solid ${C.b1}` }}>
              <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, lineHeight: 1.6 }}>
                Account settings are managed by Southern Sentinel Security. Contact your account manager to modify plan, tier, or billing details.
              </div>
            </div>
          </Card>

          <Card style={{ padding: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 4, color: C.t1 }}>Password Reset</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.6, marginBottom: 12 }}>
              Send a password reset link to {email}. The link expires after 60 minutes.
            </div>
            {resetError && (
              <div style={{ padding: 10, borderRadius: 6, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, fontSize: 10, fontFamily: FONTS.mono, marginBottom: 10 }}>
                {resetError}
              </div>
            )}
            {resetMessage && (
              <div style={{ padding: 10, borderRadius: 6, background: C.s2, border: `1px solid ${C.grn}55`, color: C.grn, fontSize: 10, fontFamily: FONTS.mono, marginBottom: 10 }}>
                {resetMessage}
              </div>
            )}
            <button
              type="button"
              onClick={handlePasswordReset}
              disabled={resetLoading || !email || email === '\u2014'}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${C.b1}`, background: resetLoading ? C.t3 : C.s2, color: C.t1, cursor: resetLoading ? 'wait' : 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONTS.sans }}
            >
              {resetLoading ? 'Sending reset link...' : 'Send reset link'}
            </button>
          </Card>
          <Card style={{ padding: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 4, color: C.t1 }}>Multi-Factor Authentication</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Tag label="Enabled" color={C.grn} />
              <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Managed by identity provider</span>
            </div>
            <div style={{ background: C.s2, borderRadius: 6, padding: 12, border: `1px solid ${C.b1}` }}>
              <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.6 }}>
                MFA is enforced through your Azure AD / Entra ID configuration. Manage MFA settings in your identity provider's admin console.
              </div>
            </div>
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 14, color: C.t1 }}>Security Settings</div>
            <Toggle label="Enforce session timeout" sub="Auto-logout after 4 hours of inactivity" on={sessions} onToggle={() => setSessions(!sessions)} />
            <Toggle label="IP allowlist" sub="Restrict access to specific IP ranges" on={ipLock} onToggle={() => setIpLock(!ipLock)} />
            {ipLock && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 5 }}>Allowed IP Ranges (one per line)</div>
                <textarea value={ipRanges} onChange={e => setIpRanges(e.target.value)} placeholder={"10.0.0.0/8\n192.168.1.0/24"} rows={3} style={{ width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '9px 11px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono, outline: 'none', resize: 'vertical' }} />
              </div>
            )}
          </Card>

          <Card style={{ padding: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 10, color: C.t1 }}>Active Sessions</div>
            <div style={{ padding: 16, background: C.s2, borderRadius: 6, border: `1px solid ${C.b1}`, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>Current session active</div>
              <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 4 }}>Session management is handled by your identity provider.</div>
            </div>
          </Card>

          <Card style={{ padding: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12, color: C.t1 }}>Permissions</div>
            {['View all clients', 'Manage findings', 'Run scans', 'Generate reports', 'Manage team members', 'Manage API keys', 'Access audit log', 'Modify settings'].map(p => (
              <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0' }}>
                <span style={{ fontSize: 10, color: C.grn }}>●</span>
                <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono }}>{p}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </Shell>
  );
}
